// The run page's header (workflow name, live status, "Run again" and "Cancel run") and its
// summary band of facts.
import { ArrowCounterClockwise, StopCircle } from "@phosphor-icons/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "rizzui";

import type { Run, Workflow } from "../../api/client";
import { messages, useT } from "../../i18n";
import type { RunStatus } from "../../proto/frames";
import { toast } from "../../layout/toast";
import { shortId } from "../../pages/format";
import { cardClass, Field } from "../../ui/Card";
import { cn } from "../../ui/cn";
import { PageHeader } from "../../ui/PageHeader";
import { StatusPill } from "../../ui/status";
import { ACTIVE_RUN, runTone } from "../../ui/tones";
import { cancelRun } from "../api";
import { clockTime, dayTime, elapsed } from "../format";
import { runTriggerLabel } from "../policy";
import { useTriggerRun } from "../useTriggerRun";
import { detailCrumbs } from "../../layout/nav";

export function RunHeader({ run, status, workflow }: { run: Run; status: string; workflow: Workflow | undefined }) {
  const { workloads } = useT();
  const t = workloads.run;
  const queryClient = useQueryClient();
  const trigger = useTriggerRun();
  const cancel = useMutation({
    mutationFn: () => cancelRun(run.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["runs", run.id] });
      toast(messages().workloads.run.cancelled);
    },
    onError: (err) => toast(err.message, "warn"),
  });
  const cancellable = status === "queued" || ACTIVE_RUN.has(status);

  return (
    <PageHeader
      title={
        <span className="flex flex-wrap items-center gap-3">
          {workflow?.name ?? workloads.runs.fallbackName}
          <span data-testid="run-status" className="inline-flex text-base">
            <StatusPill tone={runTone(status)} status={status} />
          </span>
        </span>
      }
      breadcrumb={detailCrumbs("runs", "/runs", { name: `${run.id.slice(0, 10)}…` })}
    >
      <Button variant="outline" className="gap-1.5" disabled={trigger.isPending || !workflow?.enabled} onClick={() => trigger.mutate(run.workflow_id)}>
        <ArrowCounterClockwise weight="bold" className="h-4 w-4" /> {t.runAgain}
      </Button>
      {cancellable ? (
        <Button variant="outline" color="danger" className="gap-1.5" disabled={cancel.isPending} onClick={() => cancel.mutate()}>
          <StopCircle weight="bold" className="h-4 w-4" /> {t.cancel}
        </Button>
      ) : null}
    </PageHeader>
  );
}

function workerLabel(run: Run, live: RunStatus | undefined): string {
  const t = messages().workloads.run.worker;
  if (live) return live.worker_present ? t.present : t.absent;
  if (run.worker_id) return shortId(run.worker_id);
  return run.finished_at == null ? t.notYet : t.destroyed;
}

export function RunFacts({ run, live, where }: { run: Run; live: RunStatus | undefined; where: string }) {
  const t = useT().workloads.run.facts;
  const finished = run.finished_at != null;
  return (
    <section className={cn(cardClass, "mb-5 grid grid-cols-2 gap-x-6 gap-y-4 p-5 md:grid-cols-3 xl:grid-cols-6")} aria-label={t.label}>
      <Field label={t.run} mono>
        <span title={run.id}>{run.id.slice(0, 14)}…</span>
      </Field>
      <Field label={t.where}>{where}</Field>
      <Field label={t.trigger}>
        {runTriggerLabel(run.trigger_detail)} · {dayTime(run.queued_at)}
      </Field>
      <Field label={finished ? t.took : t.runningFor}>{elapsed(run.started_at, run.finished_at)}</Field>
      <Field label={t.worker} mono>
        {workerLabel(run, live)}
      </Field>
      <Field label={t.deadline}>{run.deadline_at == null ? t.withRun : clockTime(run.deadline_at)}</Field>
    </section>
  );
}
